/**
 * Linear and time scales, and nice numbers for an axis.
 *
 * Plain TypeScript with no Svelte imports, the way `lib/map/` is, so the
 * geometry is a set of pure functions a test can call directly and the
 * component is the only thing that knows about the DOM.
 */

export interface Scale {
  /** A value in the domain -> a pixel in the range. */
  (value: number): number
  /** A pixel in the range -> a value in the domain. For the cursor. */
  invert(px: number): number
  domain: readonly [number, number]
  range: readonly [number, number]
}

/**
 * A linear scale. A zero-width domain maps to the middle of the range rather
 * than to NaN: one point, or a flat series, is an ordinary case for a
 * meteogram — five models agreeing exactly on a 60 °F afternoon is a good
 * forecast, not a degenerate chart.
 */
export function linear(domain: readonly [number, number], range: readonly [number, number]): Scale {
  const [d0, d1] = domain
  const [r0, r1] = range
  const span = d1 - d0
  const mid = (r0 + r1) / 2

  const scale = ((value: number) =>
    span === 0 ? mid : r0 + ((value - d0) / span) * (r1 - r0)) as Scale
  scale.invert = (px: number) => {
    if (r1 === r0) return d0
    return d0 + ((px - r0) / (r1 - r0)) * span
  }
  scale.domain = domain
  scale.range = range
  return scale
}

export interface TimeScale extends Scale {
  /** The same as `invert`, as a `Date`, which is what every caller wants. */
  invertTime(px: number): Date
}

/** The same scale over instants. Dates in, pixels out. */
export function time(domain: readonly [Date, Date], range: readonly [number, number]): TimeScale {
  const base = linear([domain[0].getTime(), domain[1].getTime()], range) as TimeScale
  base.invertTime = (px: number) => new Date(base.invert(px))
  return base
}

/**
 * About `target` round values covering [min, max], inclusive of the ends.
 *
 * 1 / 2 / 5 / 10, which is what makes a y axis read as 60, 65, 70 rather than
 * as 61.4, 67.2, 73.0 — the rule every axis has used since axes existed, and
 * worth having because the numbers beside a meteogram are read against each
 * other rather than against zero.
 */
export function niceTicks(min: number, max: number, target = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return []
  if (min === max) return [min]
  const step = niceStep((max - min) / Math.max(1, target))
  const first = Math.ceil(min / step) * step
  const out: number[] = []
  // A guard rather than a `while (true)`: a pathological domain must not hang
  // the render loop of a panel somebody is scrolling.
  for (let v = first, i = 0; v <= max + step * 1e-9 && i < 200; v += step, i++) {
    // Floating point leaves 69.99999999999999 where the step is 0.1.
    out.push(Number(v.toFixed(decimalsFor(step))))
  }
  return out
}

function niceStep(raw: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(Math.abs(raw) || 1))
  const normalized = raw / magnitude
  const snapped = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return snapped * magnitude
}

function decimalsFor(step: number): number {
  const exponent = Math.floor(Math.log10(Math.abs(step) || 1))
  return exponent >= 0 ? 0 : Math.min(10, -exponent)
}

/**
 * The y domain for a set of series: their union, padded, snapped to nice
 * values.
 *
 * It deliberately does **not** use the field's `plotted` range. A temperature
 * colormap plots -40 to 110 °F, and a chart of one September afternoon drawn
 * over that domain is a flat line across the middle — which is the same data
 * and none of the information.
 */
export function niceDomain(
  values: readonly (number | null | undefined)[],
  options: { padding?: number; minSpan?: number; floor?: number } = {},
): [number, number] {
  const finite = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
  if (finite.length === 0) return [0, 1]
  let min = Math.min(...finite)
  let max = Math.max(...finite)

  // A flat series still needs a band to be drawn in, or the line sits on the
  // frame and reads as an axis.
  const minSpan = options.minSpan ?? 1
  if (max - min < minSpan) {
    const mid = (min + max) / 2
    min = mid - minSpan / 2
    max = mid + minSpan / 2
  }

  const pad = (max - min) * (options.padding ?? 0.05)
  const step = niceStep((max - min + 2 * pad) / 5)
  const low = Math.floor((min - pad) / step) * step
  // An hour of rainfall cannot be -0.05 inches, and an axis that says it could
  // is a chart claiming something about the world. A field states its own
  // floor; everything else is free to pad below its minimum, because a
  // temperature at -0.05 °F is an ordinary Tuesday in Fargo.
  const floor = options.floor
  return [floor !== undefined ? Math.max(floor, low) : low, Math.ceil((max + pad) / step) * step]
}
